/**
 * Admin Dashboard Page for Re:Nav-96
 *
 * The main dashboard view for super admins.
 * Shows system statistics, quick actions, and recent activity.
 *
 * Features:
 * - Overview stats (registrations, photos pending, surveys, etc.)
 * - Quick action cards for common admin tasks
 * - Recent activity summary
 * - System health indicators
 *
 * @example
 * // In router configuration
 * { index: true, element: <AdminDashboardPage /> }
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { registrationApi, photosApi, ApiClientError } from '@/services/api';

/**
 * Stats interface for dashboard metrics
 */
interface DashboardStats {
  totalRegistrations: number;
  verifiedRegistrations: number;
  pendingPhotos: number;
  approvedPhotos: number;
  activeSurveys: number;
  totalUsers: number;
}

/**
 * Props for StatCard component
 */
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'primary' | 'accent' | 'green' | 'yellow';
  linkTo?: string;
  linkText?: string;
  trend?: {
    value: number;
    label: string;
    isPositive: boolean;
  };
}

/**
 * Stat card component displaying a single metric
 */
function StatCard({ title, value, icon, color, linkTo, linkText, trend }: StatCardProps) {
  const colorClasses = {
    primary: 'from-primary/20 to-primary/5 border-primary/20 text-primary',
    accent: 'from-accent/20 to-accent/5 border-accent/20 text-accent',
    green: 'from-green-500/20 to-green-500/5 border-green-500/20 text-green-400',
    yellow: 'from-yellow-500/20 to-yellow-500/5 border-yellow-500/20 text-yellow-400',
  };

  return (
    <div
      className={`card p-6 bg-gradient-to-br ${colorClasses[color]} border`}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}/10`}
          style={{
            background: `linear-gradient(135deg, rgba(var(--${color}-rgb), 0.2) 0%, rgba(var(--${color}-rgb), 0.05) 100%)`,
          }}
        >
          {icon}
        </div>
        {trend && (
          <span
            className={`text-xs px-2 py-1 rounded ${
              trend.isPositive
                ? 'bg-green-500/20 text-green-400'
                : 'bg-red-500/20 text-red-400'
            }`}
          >
            {trend.isPositive ? '+' : ''}{trend.value}% {trend.label}
          </span>
        )}
      </div>
      <p className="text-white/60 text-sm mb-1">{title}</p>
      <p className="text-3xl font-bold text-white">{value}</p>
      {linkTo && linkText && (
        <Link
          to={linkTo}
          className={`text-sm font-medium hover:opacity-80 transition-opacity mt-3 inline-flex items-center gap-1 ${colorClasses[color].split(' ').pop()}`}
        >
          {linkText}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      )}
    </div>
  );
}

/**
 * Props for QuickActionCard component
 */
interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  linkTo: string;
  linkText: string;
}

/**
 * Quick action card component
 */
function QuickActionCard({ title, description, icon, linkTo, linkText }: QuickActionCardProps) {
  return (
    <div className="card group hover:scale-[1.02] transition-all duration-300">
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
        style={{
          background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
          border: '1px solid rgba(233, 69, 96, 0.2)',
        }}
      >
        {icon}
      </div>
      <h3 className="text-lg font-display font-semibold text-white mb-2">{title}</h3>
      <p className="text-white/60 text-sm mb-4 leading-relaxed">{description}</p>
      <Link
        to={linkTo}
        className="text-primary text-sm font-medium hover:text-primary-light transition-colors inline-flex items-center gap-2"
      >
        {linkText}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </div>
  );
}

/**
 * Loading skeleton for stats
 */
function StatsSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="card p-6 animate-pulse">
          <div className="flex items-start justify-between mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/10" />
          </div>
          <div className="h-4 w-24 bg-white/10 rounded mb-2" />
          <div className="h-8 w-16 bg-white/20 rounded" />
        </div>
      ))}
    </div>
  );
}

/**
 * Welcome section for admin
 */
function WelcomeSection() {
  const { user } = useAuth();
  const firstName = user?.displayName?.split(' ')[0] || 'Admin';

  return (
    <div
      className="relative overflow-hidden rounded-2xl p-8"
      style={{
        background: 'linear-gradient(135deg, rgba(107, 203, 119, 0.15) 0%, rgba(45, 45, 74, 0.9) 100%)',
        border: '1px solid rgba(107, 203, 119, 0.2)',
      }}
    >
      {/* Background decoration */}
      <div
        className="absolute top-0 right-0 w-64 h-64 opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(107, 203, 119, 0.8) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-2">
          Welcome back, {firstName}!
        </h2>
        <p className="text-white/70 max-w-2xl">
          You have access to the admin panel. Manage content, approve photos, create surveys,
          and monitor the reunion website from here.
        </p>
      </div>
    </div>
  );
}

/**
 * Quick actions section
 */
function QuickActionsSection() {
  const actions: QuickActionCardProps[] = [
    {
      title: 'Manage Registrations',
      description: 'View all event registrations, export data, and send emails.',
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
          />
        </svg>
      ),
      linkTo: '/admin/registrations',
      linkText: 'View Registrations',
    },
    {
      title: 'Edit Content',
      description: 'Update dynamic content sections on the public website.',
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      ),
      linkTo: '/admin/content',
      linkText: 'Manage Content',
    },
    {
      title: 'Approve Photos',
      description: 'Review and approve member-uploaded photos for the gallery.',
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      linkTo: '/admin/photos',
      linkText: 'Review Photos',
    },
    {
      title: 'Create Survey',
      description: 'Build new surveys to gather feedback from batchmates.',
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      ),
      linkTo: '/admin/surveys/new',
      linkText: 'Create Survey',
    },
  ];

  return (
    <section>
      <h3 className="text-lg font-display font-semibold text-white mb-4">Quick Actions</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {actions.map((action) => (
          <QuickActionCard key={action.title} {...action} />
        ))}
      </div>
    </section>
  );
}

/**
 * System status section
 */
function SystemStatusSection() {
  return (
    <section className="card">
      <h3 className="text-lg font-display font-semibold text-white mb-4">System Status</h3>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-green-400" />
            <span className="text-white/80">API Server</span>
          </div>
          <span className="text-green-400 text-sm">Operational</span>
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-green-400" />
            <span className="text-white/80">Database</span>
          </div>
          <span className="text-green-400 text-sm">Connected</span>
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-green-400" />
            <span className="text-white/80">Storage</span>
          </div>
          <span className="text-green-400 text-sm">Available</span>
        </div>
      </div>
    </section>
  );
}

/**
 * Recent activity section (placeholder for future implementation)
 */
function RecentActivitySection() {
  return (
    <section className="card">
      <h3 className="text-lg font-display font-semibold text-white mb-4">Recent Activity</h3>
      <div className="text-center py-8">
        <svg
          className="w-12 h-12 text-white/20 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-white/50 text-sm">
          Activity tracking will be available in a future update.
        </p>
      </div>
    </section>
  );
}

/**
 * AdminDashboardPage Component
 *
 * The main admin dashboard displaying system statistics,
 * quick actions, and recent activity.
 *
 * @returns The admin dashboard page content
 *
 * @example
 * // Used in router
 * { index: true, element: <AdminDashboardPage /> }
 */
export function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalRegistrations: 0,
    verifiedRegistrations: 0,
    pendingPhotos: 0,
    approvedPhotos: 0,
    activeSurveys: 0,
    totalUsers: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch dashboard stats from API
   */
  const fetchStats = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch registration stats and photo stats in parallel
      const [registrationStats, photoStats] = await Promise.all([
        registrationApi.getStats().catch(() => ({ total: 0, verified: 0 })),
        photosApi.getStats().catch(() => ({ pending: 0, approved: 0 })),
      ]);

      setStats({
        totalRegistrations: 'totalRegistrations' in registrationStats
          ? registrationStats.totalRegistrations
          : ('total' in registrationStats ? registrationStats.total : 0),
        verifiedRegistrations: 'verifiedRegistrations' in registrationStats
          ? registrationStats.verifiedRegistrations
          : ('verified' in registrationStats ? registrationStats.verified : 0),
        pendingPhotos: photoStats.pending || 0,
        approvedPhotos: photoStats.approved || 0,
        activeSurveys: 0, // Placeholder - will be populated when survey API is ready
        totalUsers: 0, // Placeholder - will be populated when users API is ready
      });
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to load dashboard statistics';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Welcome section */}
      <WelcomeSection />

      {/* Error Banner */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Stats grid */}
      {isLoading ? (
        <StatsSkeleton />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Registrations"
            value={stats.totalRegistrations}
            color="primary"
            linkTo="/admin/registrations"
            linkText="View all"
            icon={
              <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                />
              </svg>
            }
          />
          <StatCard
            title="Verified"
            value={stats.verifiedRegistrations}
            color="green"
            icon={
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
          />
          <StatCard
            title="Photos Pending"
            value={stats.pendingPhotos}
            color="yellow"
            linkTo="/admin/photos"
            linkText="Review now"
            icon={
              <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            }
          />
          <StatCard
            title="Photos Approved"
            value={stats.approvedPhotos}
            color="accent"
            icon={
              <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            }
          />
        </div>
      )}

      {/* Quick actions */}
      <QuickActionsSection />

      {/* Activity and status row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentActivitySection />
        <SystemStatusSection />
      </div>
    </div>
  );
}

export default AdminDashboardPage;
